<!-- Modal: the overlay covers only the space *below* the navbar (top = navbar height, 56px collapsed /
     144px expanded), so the navbar stays clear and the dialog centers in the remaining area. The panel is
     content-sized but capped to that region (max-h-full); the body scrolls when it overflows. -->
<div class="fixed inset-x-0 bottom-0 z-[1100] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
     [style.top.px]="navbarOffset" (click)="close()">
  <div class="flex max-h-full w-full flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl sm:max-w-2xl sm:rounded-2xl"
       (click)="$event.stopPropagation()">
    <!-- Header -->
    <div class="flex flex-shrink-0 items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
      <div class="min-w-0">
        <p class="text-xs font-semibold uppercase tracking-wide text-theme-primary-600">Verträge</p>
        <h3 class="truncate text-base font-bold tracking-tight text-gray-900">{{ company.name }}</h3>
      </div>
      <button type="button" (click)="close()" aria-label="Schließen"
              class="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl text-gray-400 transition-all hover:bg-gray-100 hover:text-gray-700">
        <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z"/>
        </svg>
      </button>
    </div>

    <!-- Tabs (hidden while editing) -->
    @if (!editMode) {
      <div class="flex flex-shrink-0 gap-4 border-b border-gray-100 px-4">
        <button type="button" (click)="page = 'current'"
                class="-mb-px border-b-2 py-2.5 text-sm font-semibold transition-colors"
                [ngClass]="page === 'current' ? 'border-theme-primary-500 text-theme-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'">
          Aktueller Vertrag
        </button>
        <button type="button" (click)="page = 'history'"
                class="-mb-px inline-flex items-center gap-2 border-b-2 py-2.5 text-sm font-semibold transition-colors"
                [ngClass]="page === 'history' ? 'border-theme-primary-500 text-theme-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'">
          Verlauf
          @if (upcoming.length + past.length) {
            <span class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600">{{ upcoming.length + past.length }}</span>
          }
        </button>
      </div>
    }

    <!-- Body (scrolls) -->
    <div class="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
      @if (editMode) {
        <!-- Create / edit form -->
        <div class="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
          <div class="mb-4 border-b border-gray-100 pb-3">
            <h4 class="text-base font-bold tracking-tight text-gray-800">{{ form.id ? 'Vertrag bearbeiten' : 'Neuer Vertrag' }}</h4>
            @if (form.id) {
              <p class="mt-1 font-mono text-xs text-gray-500">{{ form.id }}</p>
            }
          </div>

          <div class="grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-6">
            <div class="sm:col-span-3">
              <label for="c-start" class="mb-2 block text-sm font-bold tracking-tight text-gray-700">Startdatum</label>
              <input type="date" id="c-start" name="c-start" [(ngModel)]="form.start"
                     class="block w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 shadow-sm transition-all duration-200 focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 sm:text-sm">
            </div>
            <div class="sm:col-span-3">
              <label for="c-end" class="mb-2 block text-sm font-bold tracking-tight text-gray-700">Enddatum</label>
              <input type="date" id="c-end" name="c-end" [(ngModel)]="form.end"
                     class="block w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 shadow-sm transition-all duration-200 focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 sm:text-sm">
            </div>
            <div class="sm:col-span-2">
              <label for="c-shards" class="mb-2 block text-sm font-bold tracking-tight text-gray-700">Anzahl Shards</label>
              <input type="number" id="c-shards" name="c-shards" min="1" max="10" step="1" [(ngModel)]="form.shard_count"
                     class="block w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-center shadow-sm transition-all duration-200 focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 sm:text-sm">
            </div>
            <div class="sm:col-span-2">
              <label for="c-devices" class="mb-2 block text-sm font-bold tracking-tight text-gray-700">Anzahl Geräte</label>
              <input type="number" id="c-devices" name="c-devices" min="1" max="100" step="1" [(ngModel)]="form.device_count"
                     class="block w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-center shadow-sm transition-all duration-200 focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 sm:text-sm">
            </div>
            <div class="sm:col-span-2">
              <label for="c-months" class="mb-2 block text-sm font-bold tracking-tight text-gray-700">Mindestlaufzeit</label>
              <input type="number" id="c-months" name="c-months" min="1" max="12" step="1" [(ngModel)]="form.device_minimum_months"
                     class="block w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-center shadow-sm transition-all duration-200 focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 sm:text-sm">
            </div>
            <!-- #664. `min="-1"`: the documented "unbegrenzt" sentinel this screen already uses for shards and
                 devices, spelled out in the hint rather than left for the reader to discover. -->
            <div class="sm:col-span-3">
              <label for="c-virtual-events" class="mb-2 block text-sm font-bold tracking-tight text-gray-700">Virtuelle
                Events (parallel)</label>
              <input type="number" id="c-virtual-events" name="c-virtual-events" min="-1" max="100" step="1"
                     [(ngModel)]="form.virtual_event_count" aria-describedby="c-virtual-events-hint"
                     class="block w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-center shadow-sm transition-all duration-200 focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 sm:text-sm">
              <p id="c-virtual-events-hint" class="mt-2 text-xs text-gray-500">-1 = unbegrenzt</p>
            </div>
          </div>
        </div>
      } @else if (page === 'current') {
        <!-- Page 1: active contract -->
        @if (current) {
        <div class="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
          <div class="flex items-start justify-between gap-3">
            <div>
              <span class="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-semibold text-green-700">Aktiver Vertrag</span>
              <p class="mt-2 text-sm text-gray-500">
                {{ current.validFrom | dayKey:'long' }} – {{ current.validTo | dayKey:'long' }}
              </p>
            </div>
            <button type="button" aria-label="Vertrag bearbeiten" (click)="editContract(current)"
                    class="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl text-gray-400 transition-all hover:bg-gray-100 hover:text-gray-700 active:scale-95">
              <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path d="M2.695 14.762l-1.262 3.155a.5.5 0 0 0 .65.65l3.155-1.262a4 4 0 0 0 1.343-.886L17.5 5.501a2.121 2.121 0 0 0-3-3L3.58 13.419a4 4 0 0 0-.885 1.343Z"/>
              </svg>
            </button>
          </div>

          <div class="mt-4 space-y-3">
            <!-- Shards meter -->
            <div>
              <div class="mb-1 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-gray-500">
                <span>Shards</span>
                <span class="text-gray-700">
                  @if (company_values) {
                    <span class="text-gray-400">{{ company_values.item1?.shardCount }} /</span>
                  }
                  {{ current.shardCount }}
                </span>
              </div>
              <div class="h-2 w-full overflow-hidden rounded-full bg-gray-100">
                <div class="h-full rounded-full transition-all duration-300"
                     [ngClass]="meterOver(company_values?.item1?.shardCount, current.shardCount) ? 'bg-red-500' : 'bg-theme-primary-500'"
                     [style.width.%]="meterPct(company_values?.item1?.shardCount, current.shardCount)"></div>
              </div>
            </div>
            <!-- Devices meter -->
            <div>
              <div class="mb-1 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-gray-500">
                <span>Geräte</span>
                <span class="text-gray-700">
                  @if (company_values) {
                    <span class="text-gray-400">{{ company_values.item1?.deviceCount }} /</span>
                  }
                  {{ current.deviceCount }}
                </span>
              </div>
              <div class="h-2 w-full overflow-hidden rounded-full bg-gray-100">
                <div class="h-full rounded-full transition-all duration-300"
                     [ngClass]="meterOver(company_values?.item1?.deviceCount, current.deviceCount) ? 'bg-red-500' : 'bg-theme-primary-500'"
                     [style.width.%]="meterPct(company_values?.item1?.deviceCount, current.deviceCount)"></div>
              </div>
            </div>
            <div class="flex items-center justify-between border-t border-gray-100 pt-3 text-sm">
              <span class="text-gray-500">Mindestlaufzeit</span>
              <span class="font-semibold text-gray-900">{{ current.minimalMonthsPerDevice }} Monate</span>
            </div>
            <!-- #664: a plain row rather than a meter — the limit is company-wide AND window-bound, so there
                 is no single "in use" number a bar could be filled from. -->
            <div class="flex items-center justify-between border-t border-gray-100 pt-3 text-sm">
              <span class="text-gray-500">Virtuelle Events (parallel)</span>
              <span class="font-semibold text-gray-900">
                {{ current.virtualEventCount == -1 ? "&infin;" : current.virtualEventCount }}
              </span>
            </div>
          </div>
        </div>
        } @else {
          <div class="rounded-2xl border-2 border-dashed border-gray-100 p-6 text-center">
            <p class="text-sm italic text-gray-500">Kein aktiver Vertrag.</p>
            <button type="button" (click)="createNew()"
                    class="mt-3 inline-flex items-center justify-center rounded-xl bg-theme-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-theme-primary-100 transition-all duration-200 hover:bg-theme-primary-700 hover:shadow-xl active:scale-95">
              Neuer Vertrag
            </button>
          </div>
        }
      } @else {
        <!-- Page 2: history (upcoming + past), scrolls -->
        @if (upcoming.length) {
          <div>
            <h4 class="mb-2 flex items-center gap-2 text-sm font-bold tracking-tight text-gray-800">
              Zukünftige Verträge
              <span class="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-600">{{ upcoming.length }}</span>
            </h4>
            <div class="space-y-2">
              @for (contract of upcoming; track contract.id) {
                <app-contract-information [contract]="contract" badge="Geplant" badgeClass="bg-blue-50 text-blue-600"
                                          (edit)="editContract($event)" [canEdit]="true"/>
              }
            </div>
          </div>
        }

        @if (past.length) {
          <div [class.mt-4]="upcoming.length">
            <h4 class="mb-2 flex items-center gap-2 text-sm font-bold tracking-tight text-gray-800">
              Vergangene Verträge
              <span class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600">{{ past.length }}</span>
            </h4>
            <div class="space-y-2">
              @for (contract of past; track contract.id) {
                <app-contract-information [contract]="contract" badge="Beendet" badgeClass="bg-gray-100 text-gray-600"
                                          (edit)="editContract($event)" [canEdit]="true"/>
              }
            </div>
          </div>
        }

        @if (!upcoming.length && !past.length) {
          <p class="py-8 text-center text-sm italic text-gray-500">Keine vergangenen oder zukünftigen Verträge.</p>
        }
      }
    </div>

    <!-- Footer -->
    <div class="flex flex-shrink-0 flex-wrap items-center gap-3 border-t border-gray-100 px-4 py-3">
      @if (!editMode) {
        <button type="button" (click)="close()"
                class="ml-auto inline-flex items-center justify-center rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 shadow-sm transition-all duration-200 hover:bg-gray-50 hover:border-gray-300 active:scale-95">
          Schließen
        </button>
        <button type="button" (click)="createNew()"
                class="inline-flex items-center justify-center rounded-xl bg-theme-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-theme-primary-100 transition-all duration-200 hover:bg-theme-primary-700 hover:shadow-xl active:scale-95">
          <svg class="mr-1.5 h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M10 5a.75.75 0 0 1 .75.75v3.5h3.5a.75.75 0 0 1 0 1.5h-3.5v3.5a.75.75 0 0 1-1.5 0v-3.5h-3.5a.75.75 0 0 1 0-1.5h3.5v-3.5A.75.75 0 0 1 10 5Z"/>
          </svg>
          Neuer Vertrag
        </button>
      } @else {
        @if (form.id) {
          <!-- `text-red-700`, not `-600`: on `bg-red-50` the 600 shade measures 4.41:1 and axe fails it at this
               14px size (style guide, "Status badge / pill"). Found by this dialog's own axe spec. -->
          <button type="button" (click)="delete()"
                  class="rounded-md bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 transition-colors hover:bg-red-100">
            Löschen
          </button>
        }
        <button type="button" (click)="cancelEdit()"
                class="ml-auto inline-flex items-center justify-center rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 shadow-sm transition-all duration-200 hover:bg-gray-50 hover:border-gray-300 active:scale-95">
          Abbrechen
        </button>
        <button type="button" (click)="save()"
                class="inline-flex items-center justify-center rounded-xl bg-theme-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-theme-primary-100 transition-all duration-200 hover:bg-theme-primary-700 hover:shadow-xl active:scale-95">
          {{ form.id ? 'Speichern' : 'Hinzufügen' }}
        </button>
      }
    </div>
  </div>
</div>
